import { flexRender } from '@tanstack/react-table'
import { cn } from 'lib/utils'
import { ArrowDownAZ, ArrowUpZA } from 'lucide-react'
import ArrowDownUp from 'modules/modular/icons/ArrowDownUp'
import { useEffect, useRef } from 'react'
import { Form, Table } from 'react-bootstrap'
import { empty } from 'utils/empty'
import { joinStyles } from 'utils/join-styles'

const sortIcons = {
    none: (
        <ArrowDownUp
            className='inline-block leading-none ml-2 cursor-pointer'
            style={{ height: '1em', width: '1em' }}
        />
    ),
    asc: (
        <ArrowDownAZ
            className='inline-block leading-none ml-2 cursor-pointer'
            style={{ height: '1em', width: '1em' }}
        />
    ),
    desc: (
        <ArrowUpZA
            className='inline-block leading-none ml-2 cursor-pointer'
            style={{ height: '1em', width: '1em' }}
        />
    ),
}
const headerAlignStyles = {
    left: 'text-start',
    center: 'text-center',
    right: 'text-end',
}
export function DataTable({
    table,
    focusedRow,
    enableRowNavigation,
    keyboardNavigation,
    onRowKeyUp,
    defaultAlignHeader,
    className,
}) {
    const rowRefs = useRef([])

    useEffect(() => {
        if (focusedRow !== undefined && enableRowNavigation) {
            rowRefs.current[focusedRow]?.value.focus()
            rowRefs.current[focusedRow]?.value.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            })
        }
    }, [focusedRow, enableRowNavigation])

    const handleRowKeyDown = (e, index) => {
        const upRowKey = (keyboardNavigation?.upRowKey ?? 'ArrowUp').toLowerCase()
        const downRowKey = (keyboardNavigation?.downRowKey ?? 'ArrowDown').toLowerCase()

        const key = e.key.toLowerCase()
        if (key === upRowKey) {
            e.preventDefault()
            rowRefs.current[index]?.prev?.focus()
            rowRefs.current[index]?.prev?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            })
        } else if (key === downRowKey) {
            e.preventDefault()
            rowRefs.current[index]?.next?.focus()
            rowRefs.current[index]?.next?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            })
        }
    }

    const handleRowKeyUp = (e, row) => {
        const firstRowKey = (keyboardNavigation?.firstRowKey ?? 'Home').toLowerCase()
        const lastRowKey = (keyboardNavigation?.lastRowKey ?? 'End').toLowerCase()

        const key = e.key.toLowerCase()
        if (key === firstRowKey) {
            e.preventDefault()
            rowRefs.current[0]?.value.focus()
            rowRefs.current[0]?.value.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            })
        } else if (key === lastRowKey) {
            e.preventDefault()
            rowRefs.current[rowRefs.current.length - 1]?.value.focus()
            rowRefs.current[rowRefs.current.length - 1]?.value.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            })
        }
        onRowKeyUp?.(e, row)
    }

    return (
        <Table className={className}>
            <thead className={cn(defaultAlignHeader && headerAlignStyles[defaultAlignHeader])}>
                {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                            const meta = header.column.columnDef.meta ?? {}
                            const canSort = header.column.getCanSort()
                            const isSorted = header.column.getIsSorted()
                            return (
                                <th
                                    key={header.id}
                                    colSpan={header.colSpan}
                                    onClick={
                                        canSort ? () => header.column.toggleSorting() : undefined
                                    }
                                    className={cn(
                                        'data-state:cursor-pointer data-[state=sorted]:text-blue-500',
                                        meta.className,
                                        meta.headerClassName,
                                    )}
                                    style={joinStyles(meta.style, meta.headerStyles)}
                                    data-state={
                                        canSort ? (isSorted ? 'sorted' : 'unsorted') : undefined
                                    }
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                              header.column.columnDef.header,
                                              header.getContext(),
                                          )}
                                    {canSort && sortIcons[isSorted || 'none']}
                                </th>
                            )
                        })}
                    </tr>
                ))}
                {table.options.enableColumnFilters && (
                    <tr>
                        {table.getVisibleLeafColumns().map((col) => {
                            const meta = col.columnDef.meta ?? {}
                            const value = col.getFilterValue() ?? ''
                            const canFilter = col.columnDef.enableColumnFilter ?? true
                            return (
                                <th
                                    key={col.id}
                                    className={cn('py-2', meta.className, meta.filterClassName)}
                                    style={joinStyles(meta.style, meta.filterStyles)}
                                >
                                    {canFilter &&
                                        (meta.filterElement ? (
                                            meta.filterElement({
                                                value,
                                                filterCallback: col.setFilterValue,
                                            })
                                        ) : (
                                            <Form.Control
                                                value={value}
                                                onChange={(e) => col.setFilterValue(e.target.value)}
                                            />
                                        ))}
                                </th>
                            )
                        })}
                    </tr>
                )}
            </thead>
            <tbody>
                {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row, index) => {
                        return (
                            <tr
                                key={row.id}
                                ref={(el) => {
                                    if (index === 0) rowRefs.current = []
                                    if (el) {
                                        const prev = rowRefs.current[index - 1]
                                        rowRefs.current[index] = {
                                            prev: prev?.value ?? null,
                                            value: el,
                                            next: null,
                                        }
                                        if (prev) prev.next = el
                                    }
                                }}
                                data-state={row.getIsSelected() ? 'selected' : undefined}
                                className='focus:outline-0 focus:bg-muted'
                                tabIndex={enableRowNavigation ? 0 : undefined}
                                onKeyDown={
                                    enableRowNavigation
                                        ? (e) => handleRowKeyDown(e, index)
                                        : undefined
                                }
                                onKeyUp={
                                    enableRowNavigation ? (e) => handleRowKeyUp(e, row) : undefined
                                }
                            >
                                {row.getVisibleCells().map((cell) => {
                                    const meta = cell.column.columnDef.meta ?? {}
                                    return (
                                        <td
                                            key={cell.id}
                                            className={cn(meta.className, meta.cellClassName)}
                                            style={joinStyles(meta.style, meta.cellStyles)}
                                        >
                                            {empty(cell.getValue()) && meta.default
                                                ? meta.default
                                                : flexRender(
                                                      cell.column.columnDef.cell,
                                                      cell.getContext(),
                                                  )}
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })
                ) : (
                    <tr>
                        <td
                            colSpan={table.getVisibleLeafColumns().length}
                            className='h-24 text-center'
                        >
                            Sin registros.
                        </td>
                    </tr>
                )}
            </tbody>
        </Table>
    )
}
